import { Link } from 'gatsby'
import { InView } from 'react-intersection-observer'
import type { CommercialBenefit } from 'src/components/product-page/context/types/product.type'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { Image } from 'src/components/ui/Image'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { sendProductInformationSelectedEvent } from 'src/utils/amplitude/sendProductInformationSelectedEvent'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'

import { useProductPageContext } from '../../contexts'
import { ReturnsBenefitIcon } from './ReturnsBenefitIcon'

const RETURNS_BENEFIT_TEXT = 'Trocas e devoluções grátis em até 45 dias'
const DEFAULT_MODAL_LINK_TEXT = 'Saiba mais'

type Props = {
  item: CommercialBenefit
  onClickButton: (item: CommercialBenefit) => void
  isMobileFirstFoldRevamp?: boolean
}

type StyleVariant = 'default' | 'revamp'

const STYLES = {
  wrapper: {
    default: 'flex gap-2',
    revamp: 'flex items-center gap-1',
  },
  image: {
    default: 'self-start mt-1',
    revamp: 'self-center',
  },
  content: {
    default: '',
    revamp: 'flex flex-col gap-1',
  },
  text: {
    default: 'desktop-body-regular-text3 text-restructure-primary',
    revamp: 'text-xs font-medium leading-[12px] text-[#383838]',
  },
  link: {
    default: 'desktop-caption-regular text-tertiary underline mt-2',
    revamp: 'text-xs font-medium leading-none text-tertiary underline mt-1',
  },
} as const

const isReturnsBenefit = (benefit: CommercialBenefit) =>
  benefit?.text === RETURNS_BENEFIT_TEXT

const getBenefitLinkText = (
  benefit: CommercialBenefit,
  isModalAction: boolean
) => benefit.link.text?.trim() || (isModalAction ? DEFAULT_MODAL_LINK_TEXT : '')

export function BenefitsItem({
  item,
  onClickButton,
  isMobileFirstFoldRevamp = false,
}: Props) {
  const styleVariant: StyleVariant = isMobileFirstFoldRevamp
    ? 'revamp'
    : 'default'

  const { currentSku } = useProductPageContext()
  const { pageData } = useGlobalContext()
  const isNoBoxBenefit = item?.name === 'Sem Caixa'

  function sendClickEvent() {
    const standardObject = getStandardObject(pageData)
    const featureDetailsObject = getFeatureObject({
      locationOnPage: null,
      name: 'Produto sem caixa',
      section: 'produto',
    })

    const productObject = getProductQueryObject(currentSku)

    sendProductInformationSelectedEvent({
      clickedElement: 'Produto sem caixa',
      featureDetailsObject,
      productObject,
      standardObject,
    })
  }

  const sendViewEvent = () => {
    const featureObject = getFeatureObject({
      section: 'produto',
      locationOnPage: 'null',
      name: 'Produto sem caixa',
    })

    const eventData = {
      ...getStandardObject(pageData),
      ...featureObject,
    }

    dispatchAmplitudeEvent({ eventName: 'Product Feature Viewed', eventData })
  }

  const handleClick = () => {
    if (isNoBoxBenefit) {
      sendClickEvent()
    }

    onClickButton(item)
  }

  const isReturns = isReturnsBenefit(item)
  const shouldUseRevampReturnsIcon = isMobileFirstFoldRevamp && isReturns

  const hasOpenModalAction = item.link.action === 'open-modal'
  const benefitLinkText = getBenefitLinkText(item, hasOpenModalAction)

  const shouldHideLinkOnRevamp = isMobileFirstFoldRevamp && isReturns
  const shouldRenderLink = Boolean(benefitLinkText) && !shouldHideLinkOnRevamp
  const textClassName = `${STYLES.text[styleVariant]} ${
    shouldUseRevampReturnsIcon ? 'whitespace-nowrap' : ''
  }`

  return (
    <InView
      as="div"
      triggerOnce
      onChange={(inView) => {
        if (inView && isNoBoxBenefit) {
          sendViewEvent()
        }
      }}
      className={STYLES.wrapper[styleVariant]}
    >
      {shouldUseRevampReturnsIcon ? (
        <ReturnsBenefitIcon
          className="self-center shrink-0"
          aria-hidden="true"
        />
      ) : (
        <Image
          className={STYLES.image[styleVariant]}
          src={item.icon}
          alt={item.name}
          width={isMobileFirstFoldRevamp ? 20 : 24}
          height={isMobileFirstFoldRevamp ? 20 : 24}
        />
      )}
      <div className={STYLES.content[styleVariant]}>
        <p className={textClassName}>{item.text}</p>
        {shouldRenderLink &&
          (hasOpenModalAction ? (
            <button onClick={handleClick} className={STYLES.link[styleVariant]}>
              {benefitLinkText}
            </button>
          ) : (
            <Link
              to={item.link.url ?? ''}
              className={STYLES.link[styleVariant]}
            >
              {benefitLinkText}
            </Link>
          ))}
      </div>
    </InView>
  )
}
